import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../assets/styles/PressPage.css';

interface PressRelease {
  id: number;
  title: string;
  date: string;
  excerpt: string;
  slug: string;
}

interface MediaMention {
  id: number;
  title: string;
  publication: string;
  date: string;
  url: string;
  logo: string;
}

const PressPage: React.FC = () => {
  // Sample press releases
  const pressReleases: PressRelease[] = [
    {
      id: 1,
      title: "Translate Stocks Launches AI-Powered Trading Platform with Price on Rails Technology",
      date: "March 15, 2024",
      excerpt: "Translate Stocks announced today the launch of its revolutionary AI-powered trading platform featuring the innovative Price on Rails technology, designed to predict market movements and provide traders with unparalleled insights.",
      slug: "translate-stocks-launches-ai-powered-trading-platform"
    },
    {
      id: 2,
      title: "Translate Stocks Introduces Agentic Bot for Automated Trading",
      date: "January 10, 2024",
      excerpt: "Building on the success of its Price on Rails technology, Translate Stocks has unveiled the Agentic Bot, an automated trading companion that works seamlessly within the platform to execute trades based on AI-generated signals.",
      slug: "translate-stocks-introduces-agentic-bot"
    },
    {
      id: 3,
      title: "Translate Stocks Secures $12 Million in Series A Funding",
      date: "October 20, 2023",
      excerpt: "Translate Stocks has secured $12 million in Series A funding led by Fintech Ventures, with participation from Trading Capital Partners and several angel investors, to accelerate the development of its AI trading technology.",
      slug: "translate-stocks-secures-series-a-funding"
    },
    {
      id: 4,
      title: "Translate Stocks Named Among Top 10 Fintech Startups to Watch",
      date: "August 5, 2023",
      excerpt: "Translate Stocks has been recognized as one of the Top 10 Fintech Startups to Watch by Financial Innovation Magazine, highlighting the company's innovative approach to AI-powered trading technology.",
      slug: "translate-stocks-named-top-fintech-startup"
    }
  ];

  // Sample media mentions
  const mediaMentions: MediaMention[] = [
    {
      id: 1,
      title: "How AI is Transforming Day Trading for Retail Investors",
      publication: "Financial Times",
      date: "February 28, 2024",
      url: "https://www.ft.com/content/ai-trading-retail-investors",
      logo: "/images/ft-logo.svg"
    },
    {
      id: 2,
      title: "Price on Rails: The Next Generation of Predictive Trading Tools",
      publication: "TechCrunch",
      date: "January 25, 2024",
      url: "https://techcrunch.com/2024/01/25/price-on-rails-predictive-trading/",
      logo: "/images/techcrunch-logo.svg"
    },
    {
      id: 3,
      title: "Translate Stocks CEO Discusses Future of AI in Financial Markets",
      publication: "Bloomberg",
      date: "December 12, 2023",
      url: "https://www.bloomberg.com/news/articles/translate-stocks-ceo-interview",
      logo: "/images/bloomberg-logo.svg"
    },
    {
      id: 4,
      title: "Review: We Tested Translate Stocks' Price on Rails Technology",
      publication: "Wall Street Journal",
      date: "November 5, 2023",
      url: "https://www.wsj.com/articles/translate-stocks-price-on-rails-review",
      logo: "/images/wsj-logo.svg"
    },
    {
      id: 5,
      title: "Startup Spotlight: Translate Stocks and the Future of Trading",
      publication: "Forbes",
      date: "September 18, 2023",
      url: "https://www.forbes.com/sites/startupspotlight/translate-stocks",
      logo: "/images/forbes-logo.svg"
    },
    {
      id: 6,
      title: "How Agentic Technologies Are Changing Financial Decision-Making",
      publication: "CNBC",
      date: "August 22, 2023",
      url: "https://www.cnbc.com/2023/08/22/agentic-technologies-financial-decision-making.html",
      logo: "/images/cnbc-logo.svg"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Press & Media | Translate Stocks</title>
        <meta name="description" content="Access the latest press releases, media coverage, brand assets, and contact information for Translate Stocks and our AI-powered Price on Rails trading platform." />
        <meta name="keywords" content="translate stocks press, price on rails news, AI trading media, fintech news" />
      </Helmet>
      
      <Header />
      
      <main className="press-page">
        <section className="hero-section press-hero">
          <div className="container">
            <div className="hero-content">
              <h1>Press & <span className="highlight">Media</span></h1>
              <p className="hero-subtitle">
                Get the latest news, press releases, and media resources for Translate Stocks 
                and our Price on Rails trading platform.
              </p>
            </div>
          </div>
        </section>
        
        <section className="press-contact">
          <div className="container">
            <div className="press-contact-wrapper">
              <div className="press-contact-content">
                <h2>Media Inquiries</h2>
                <p>
                  For press inquiries, interview requests, or additional information about Translate Stocks, 
                  please contact our media relations team.
                </p>
                <div className="contact-details">
                  <div className="contact-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="#2563EB"/>
                    </svg>
                    <a href="mailto:press@translatestocks.com">press@translatestocks.com</a>
                  </div>
                  <div className="contact-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 15.5C18.8 15.5 17.5 15.3 16.4 14.9C16.3 14.9 16.2 14.9 16.1 14.9C15.8 14.9 15.6 15 15.4 15.2L13.2 17.4C10.4 15.9 8 13.6 6.6 10.8L8.8 8.6C9.1 8.3 9.2 7.9 9 7.6C8.7 6.5 8.5 5.2 8.5 4C8.5 3.5 8 3 7.5 3H4C3.5 3 3 3.5 3 4C3 13.4 10.6 21 20 21C20.5 21 21 20.5 21 20V16.5C21 16 20.5 15.5 20 15.5ZM19 12H21C21 7 17 3 12 3V5C15.9 5 19 8.1 19 12ZM15 12H17C17 9.2 14.8 7 12 7V9C13.7 9 15 10.3 15 12Z" fill="#2563EB"/>
                    </svg>
                    <a href="tel:+18005551002">+1 (800) 555-1002</a>
                  </div>
                </div>
              </div>
              
              <div className="press-spokesperson">
                <img 
                  src="/images/press-contact.jpg" 
                  alt="Translate Stocks Media Relations" 
                  width="300" 
                  height="300"
                  loading="lazy"
                />
                <div className="spokesperson-info">
                  <h3>Jessica Williams</h3>
                  <p>Director of Communications</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section className="press-releases">
          <div className="container">
            <h2 className="section-title">Press Releases</h2>
            
            <div className="releases-list">
              {pressReleases.map(release => (
                <div key={release.id} className="press-release-card">
                  <span className="release-date">{release.date}</span>
                  <h3 className="release-title">
                    <Link to={`/press/${release.slug}`}>{release.title}</Link>
                  </h3>
                  <p className="release-excerpt">{release.excerpt}</p>
                  <Link to={`/press/${release.slug}`} className="read-more">
                    Read Full Release
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                </div>
              ))}
            </div>
            
            <div className="view-all">
              <Link to="/press/archive" className="btn btn-outline">View All Press Releases</Link>
            </div>
          </div>
        </section>
        
        <section className="media-coverage">
          <div className="container">
            <h2 className="section-title">Media Coverage</h2>
            
            <div className="media-grid">
              {mediaMentions.map(mention => (
                <a 
                  key={mention.id} 
                  href={mention.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="media-card"
                >
                  <div className="media-logo">
                    <img 
                      src={mention.logo} 
                      alt={mention.publication} 
                      width="120" 
                      height="40"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="media-title">{mention.title}</h3>
                  <div className="media-meta">
                    <span className="media-publication">{mention.publication}</span>
                    <span className="media-date">{mention.date}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
        
        <section className="press-kit">
          <div className="container">
            <div className="press-kit-wrapper">
              <div className="press-kit-content">
                <h2>Press Kit & Brand Assets</h2>
                <p>
                  Download official logos, product screenshots, executive headshots, and brand guidelines 
                  for media use. For additional assets or specific requests, please contact our media 
                  relations team.
                </p>
                <div className="press-kit-actions">
                  <a href="/files/translate-stocks-press-kit.zip" className="btn btn-primary">
                    Download Press Kit
                  </a>
                  <a href="/brand-guidelines" className="btn btn-outline">
                    View Brand Guidelines
                  </a>
                </div>
              </div>
              
              <div className="press-kit-preview">
                <div className="asset-grid">
                  <div className="asset-item">
                    <img 
                      src="/images/logo-preview.jpg" 
                      alt="Translate Stocks Logo" 
                      width="120" 
                      height="120"
                      loading="lazy"
                    />
                  </div>
                  <div className="asset-item">
                    <img 
                      src="/images/product-screenshot-preview.jpg" 
                      alt="Product Screenshot" 
                      width="120" 
                      height="120"
                      loading="lazy"
                    />
                  </div>
                  <div className="asset-item">
                    <img 
                      src="/images/headshot-preview.jpg" 
                      alt="Executive Headshot" 
                      width="120" 
                      height="120"
                      loading="lazy"
                    />
                  </div>
                  <div className="asset-item">
                    <img 
                      src="/images/brand-colors-preview.jpg" 
                      alt="Brand Colors" 
                      width="120" 
                      height="120"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section className="company-facts">
          <div className="container">
            <h2 className="section-title">Company Facts</h2>
            
            <div className="facts-grid">
              <div className="fact-card">
                <h3>About Translate Stocks</h3>
                <p>
                  Translate Stocks is a leading provider of AI-powered trading technology, founded in 2020 
                  and headquartered in New York City. Our flagship product, Price on Rails, uses advanced 
                  machine learning to predict market movements and help traders make faster, more informed decisions.
                </p>
              </div>
              
              <div className="fact-card">
                <h3>Our Technology</h3>
                <p>
                  The Price on Rails technology analyzes vast amounts of market data in real-time to project 
                  potential price movements. Our Agentic Bot builds on this foundation to offer automated 
                  trading capabilities that work seamlessly alongside human decision-making.
                </p>
              </div>
              
              <div className="fact-card">
                <h3>Leadership</h3>
                <p>
                  Translate Stocks was founded by Michael Zhang (CEO), Sarah Johnson (CTO), and David Mercer (CPO), 
                  bringing together expertise from quantitative finance, artificial intelligence, and product development.
                </p>
              </div>
              
              <div className="fact-card">
                <h3>Funding</h3>
                <p>
                  The company has raised $15 million to date, including a $12 million Series A round in October 2023 
                  led by Fintech Ventures with participation from Trading Capital Partners and angel investors.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
    </>
  );
};

export default PressPage;